// Port of Offline/OfflinePackagePlanner.swift (TrailBivvyKit) and the compact `StoredPlan` of OfflineStore.swift:
// offline packages along a route corridor.
//
// Web deviations:
// - Only `offlineCapable` layers are planned (Swift: `allowsOfflineStorage`); a layer whose host sends no CORS headers
//   cannot be stored from the page.
// - The data part of a PWA package is the pinned trail pack JSON + world rules layer (not a re-encoded TrailDataset):
//   its size is passed as `dataBytes` (catalogue `packBytes` + rules size) instead of a dataset.
// - The persisted plan drops each layer's coverage polygons (its tiles are already filtered; resume and cleanup do not
//   need them) to keep IndexedDB records small.

import type { EpochMs, GeoBounds, OfflinePackageLevel, OfflineRegion, TrailRoute } from '../core/models';
import { offlineCorridorHalfWidth, offlineLevelZoomRange } from '../core/domain';
import { boundsExpanded, polylineBounds, polylineCoordinates } from '../core/geo';
import { CoverageArea, coverageMarginMeters, type CoverageRelation } from './CoverageArea';
import { layerCoverageArea, layerZoomRange, type TileLayerDescriptor } from './TileLayerDescriptor';
import { TileMath, tileAncestor, tileIsValid, tileKey, type TileCoordinate } from './TileMath';

/** Everything needed to download one offline region: the region record, the tiles per layer and size estimates. */
export interface OfflinePackagePlan {
  /** The region to create (status `notDownloaded`, `totalTiles` and `estimatedBytes` filled in). */
  region: OfflineRegion;
  /** Tiles to download, keyed by layer id; each list is ordered by zoom, then row, then column. */
  layerTiles: Record<string, TileCoordinate[]>;
  /**
   * Layers included in the package: offline-capable layers overlapping the level's zooms and (for layers with a
   * coverage limit) having at least one tile in the corridor.
   */
  layers: TileLayerDescriptor[];
  /** Sum of all tile counts. */
  totalTiles: number;
  /** Σ tiles × layer average tile size. */
  estimatedTileBytes: number;
  /** Route data size (pinned pack + rules when known, heuristic otherwise). */
  estimatedDataBytes: number;
}

export interface PlanRequest {
  route: TrailRoute;
  regionID: string;
  name: string;
  level: OfflinePackageLevel;
  layers: readonly TileLayerDescriptor[];
  /** Bytes of the route data pinned with the package (pack JSON + world rules), when known. */
  dataBytes?: number;
  now: EpochMs;
}

const zeroBounds: GeoBounds = { minLatitude: 0, minLongitude: 0, maxLatitude: 0, maxLongitude: 0 };

/** Plans offline packages along a route corridor. */
export const OfflinePackagePlanner = {
  /** Fixed allowance for POIs, protected areas and regulations when no data size is given (bytes). */
  baseDataAllowance: 512_000,
  /** Allowance per route vertex (JSON-encoded geometry with elevation) when no data size is given (bytes). */
  bytesPerRoutePoint: 64,

  /**
   * Corridor = route polyline ± `offlineCorridorHalfWidth(level)`; tiles for each offline-capable layer within the
   * level's zoom range ∩ layer zooms (no tiles at all for `routeOnly`). Duplicate layer ids are planned once.
   *
   * Coverage: a layer declaring `coverage`/`coveragePolygons` only gets the corridor tiles inside (or within
   * `coverageMarginMeters` of) that area — a Tour du Mont Blanc package never requests swisstopo tiles in France or
   * Italy. A coverage-limited layer without any such tile is left out of the plan. Size estimates use the same
   * filtered tiles. The region's bounds are the route bounds expanded by the corridor half-width.
   */
  plan(request: PlanRequest): OfflinePackagePlan {
    const { route, regionID, name, level, layers, now } = request;
    const coordinates = polylineCoordinates(route.polyline);
    const halfWidth = offlineCorridorHalfWidth(level);

    const layerTiles: Record<string, TileCoordinate[]> = {};
    const includedLayers: TileLayerDescriptor[] = [];
    let tileBytes = 0;

    const zoomRange = offlineLevelZoomRange(level);
    if (zoomRange && coordinates.length > 0) {
      const tilesPerZoom = new Map<number, TileCoordinate[]>();
      const seenLayerIDs = new Set<string>();
      for (const layer of layers) {
        if (!layer.offlineCapable || seenLayerIDs.has(layer.id)) continue;
        seenLayerIDs.add(layer.id);
        const native = layerZoomRange(layer);
        const low = Math.max(zoomRange.min, native.min);
        const high = Math.min(zoomRange.max, native.max);
        if (low > high) continue;
        const coverage = layerCoverageArea(layer);
        const tiles: TileCoordinate[] = [];
        let parentRelations = new Map<string, CoverageRelation>();
        for (let zoom = low; zoom <= high; zoom++) {
          let zoomTiles = tilesPerZoom.get(zoom);
          if (!zoomTiles) {
            zoomTiles = TileMath.tilesAlongCorridor(coordinates, halfWidth, zoom).sort((a, b) => a.y - b.y || a.x - b.x);
            tilesPerZoom.set(zoom, zoomTiles);
          }
          if (coverage.isWorldwide) {
            for (const tile of zoomTiles) tiles.push(tile);
          } else {
            parentRelations = OfflinePackagePlanner.coveredTiles(zoomTiles, coverage, parentRelations, tiles);
          }
        }
        if (!coverage.isWorldwide && tiles.length === 0) continue;
        layerTiles[layer.id] = tiles;
        includedLayers.push(layer);
        tileBytes += tiles.length * Math.max(0, layer.averageTileBytes);
      }
    }

    const totalTiles = Object.values(layerTiles).reduce((sum, tiles) => sum + tiles.length, 0);
    const dataBytes = OfflinePackagePlanner.estimatedDataBytes(route, request.dataBytes);
    const routeBounds = polylineBounds(route.polyline);
    const bounds = routeBounds ? boundsExpanded(routeBounds, halfWidth) : { ...zeroBounds };
    const region: OfflineRegion = {
      id: regionID,
      name,
      trailID: route.trailID,
      stageIDs: [...route.stageIDs],
      bounds,
      level,
      estimatedBytes: tileBytes + dataBytes,
      downloadedBytes: 0,
      status: 'notDownloaded',
      createdAt: now,
      completedTiles: 0,
      totalTiles,
    };
    if (region.trailID === undefined) delete region.trailID;
    return {
      region,
      layerTiles,
      layers: includedLayers,
      totalTiles,
      estimatedTileBytes: tileBytes,
      estimatedDataBytes: dataBytes,
    };
  },

  /**
   * Estimated download size of a package (tiles + route data heuristic), identical to `plan(...).region.estimatedBytes`
   * without a data size (so tiles outside a layer's coverage are not counted).
   */
  estimatedBytes(route: TrailRoute, level: OfflinePackageLevel, layers: readonly TileLayerDescriptor[]): number {
    return OfflinePackagePlanner.plan({ route, regionID: route.id, name: route.name, level, layers, now: 0 }).region
      .estimatedBytes;
  },

  /**
   * Appends the tiles of one zoom level that `coverage` covers (with `coverageMarginMeters`) and returns every tile's
   * relation for the next level. A tile whose parent (one level up) was entirely inside or entirely outside inherits
   * that answer, so only tiles along the coverage boundary are tested.
   */
  coveredTiles(
    zoomTiles: readonly TileCoordinate[],
    coverage: CoverageArea,
    parentRelations: ReadonlyMap<string, CoverageRelation>,
    into: TileCoordinate[],
  ): Map<string, CoverageRelation> {
    const relations = new Map<string, CoverageRelation>();
    for (const tile of zoomTiles) {
      const parent = tileAncestor(tile, tile.z - 1);
      const known = parent ? parentRelations.get(tileKey(parent)) : undefined;
      const relation =
        known !== undefined && known !== 'partial'
          ? known
          : coverage.relation(CoverageArea.searchBox(tile, coverageMarginMeters));
      relations.set(tileKey(tile), relation);
      if (relation !== 'outside') into.push(tile);
    }
    return relations;
  },

  /** The known size of the pinned data when given, else a heuristic from the route length. */
  estimatedDataBytes(route: TrailRoute, dataBytes?: number): number {
    if (dataBytes !== undefined && Number.isFinite(dataBytes) && dataBytes >= 0) return Math.round(dataBytes);
    return OfflinePackagePlanner.baseDataAllowance + route.polyline.points.length * OfflinePackagePlanner.bytesPerRoutePoint;
  },
};

// MARK: - Compact persisted form (Swift `StoredPlan`)

/** Compact form of a plan kept in IndexedDB: tiles as flat `[z, x, y, z, x, y, …]` arrays per layer. */
export interface StoredPlan {
  layers: TileLayerDescriptor[];
  tiles: Record<string, number[]>;
  estimatedTileBytes: number;
  estimatedDataBytes: number;
}

export function storedPlanFrom(plan: OfflinePackagePlan): StoredPlan {
  const tiles: Record<string, number[]> = {};
  for (const [layerID, list] of Object.entries(plan.layerTiles)) {
    const flat = new Array<number>(list.length * 3);
    list.forEach((t, i) => {
      flat[i * 3] = t.z;
      flat[i * 3 + 1] = t.x;
      flat[i * 3 + 2] = t.y;
    });
    tiles[layerID] = flat;
  }
  const layers = plan.layers.map((layer) => {
    const copy = { ...layer };
    delete copy.coveragePolygons;
    return copy;
  });
  return { layers, tiles, estimatedTileBytes: plan.estimatedTileBytes, estimatedDataBytes: plan.estimatedDataBytes };
}

/** Rebuilds a plan from its stored form and the current region record (invalid tile triples are dropped). */
export function planFromStored(stored: StoredPlan, region: OfflineRegion): OfflinePackagePlan {
  const layerTiles: Record<string, TileCoordinate[]> = {};
  for (const [layerID, flat] of Object.entries(stored.tiles)) {
    const list: TileCoordinate[] = [];
    for (let i = 0; i + 2 < flat.length; i += 3) {
      const tile = { z: flat[i], x: flat[i + 1], y: flat[i + 2] };
      if (tileIsValid(tile)) list.push(tile);
    }
    layerTiles[layerID] = list;
  }
  const totalTiles = Object.values(layerTiles).reduce((sum, list) => sum + list.length, 0);
  return {
    region,
    layerTiles,
    layers: stored.layers,
    totalTiles,
    estimatedTileBytes: stored.estimatedTileBytes,
    estimatedDataBytes: stored.estimatedDataBytes,
  };
}
